import { Breadcrumb } from '@e3dc-react/shell/Elements/Header/Breadcrumbs'
import { Skeleton } from '@mui/material'
import { Stack } from '@mui/system'
import { UseQueryResult } from '@tanstack/react-query'
import React from 'react'
import { Route, Routes, useParams } from 'react-router-dom'
import { InstallationDevices } from 'src/Libs/flow/types/installation'
import { InstallationConfigDto, InstallationDto, MyPvControllerDto, SubDevicesDto } from '../../../installation/api'
import { useInstallation, useInstallationConfig, useInstallationDevices, useInstallationSubDevices } from '../../../installation/hooks'
import { parseMyPvIo } from '../../../parsers'
import { BadgeDto, BadgesPage, useGetBadges } from '../../badges-for-charging-station'
import { BoilerPageWrapper } from '../../boiler/pages'
import StoragePage from '../../energy-storage-management/pages/StoragePage'
import { SolarConsumptionPriorityPage } from '../../solar'
import { WallboxPage } from '../../wallbox'
import { SETTINGS_ROUTES } from '../constants'

/**
 * Routes to different end user settings.
 */
const EndUserSettingsRoutes: React.FC<{ parentBreadcrumb: Breadcrumb }> = ({ parentBreadcrumb }) => {
  const { installationId, storageId } = useParams<{ installationId: string; storageId: string }>()
  const installationIdNumber = installationId ? Number.parseInt(installationId) : null

  if (installationIdNumber === null || storageId === undefined) {
    throw new Error('Installation ID is required for simple settings')
  }

  const { installationConfig, badges, devices, subDevices, isLoading } = useInstallationQuery(installationIdNumber)

  if (isLoading)
    return (
      <Stack gap={1} p={2} m={'auto'}>
        <Skeleton variant="rounded" height={200} />
        <Skeleton variant="rounded" height={124} />
        <Skeleton variant="rounded" height={124} />
        <Skeleton variant="rounded" height={124} />
      </Stack>
    )

  const hasPairedStorage = devices.data?.storages?.some(storage => storage.pairingState === 'paired') || false
  const hasNoStorage = devices.data?.storages?.length === 0 || false

  const pairedEvses = subDevices.data?.controlled?.filter(evse => evse.pairingState === 'paired') ?? []

  const hasPairedWallbox = !!pairedEvses.length

  const myPv = (subDevices.data?.controllers?.find(device => device.type === 'MyPvHeater') as Nullable<MyPvControllerDto>) ?? null
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const hasPairedMyPv = myPv !== null && myPv.pairingState === 'paired'
  const parsedMyPv = hasPairedMyPv ? parseMyPvIo(myPv) : null

  const isSinglePhase = !!installationConfig.data && installationConfig.data?.phaseCount === 1

  return (
    <Routes>
      <Route
        path={SETTINGS_ROUTES.solarPriority}
        element={
          <SolarConsumptionPriorityPage
            installationId={installationIdNumber}
            installationConfig={installationConfig.data!}
            queryIsLoading={installationConfig.isFetching}
            hasStorage={hasPairedStorage}
            hasWallbox={hasPairedWallbox}
          />
        }
      />
      <Route
        path={SETTINGS_ROUTES.badges}
        element={<BadgesPage installationId={installationIdNumber} badges={badges.data?.filter(item => item !== null) || null} />}
      />

      <>
        <Route
          path={`${SETTINGS_ROUTES.wallbox}/:evseId`}
          element={
            <WallboxPage
              installationId={installationIdNumber}
              pairedEvses={pairedEvses}
              devicesAreLoading={devices.isFetching}
              isSinglePhase={isSinglePhase}
              storageId={storageId}
              parentBreadcrumb={parentBreadcrumb}
            />
          }
        />
        <Route
          path={SETTINGS_ROUTES.wallbox}
          element={
            <WallboxPage
              installationId={installationIdNumber}
              pairedEvses={pairedEvses}
              devicesAreLoading={devices.isFetching}
              isSinglePhase={isSinglePhase}
              storageId={storageId}
              parentBreadcrumb={parentBreadcrumb}
            />
          }
        />
      </>

      <Route
        path={SETTINGS_ROUTES.storage}
        element={
          <StoragePage
            installationId={installationIdNumber}
            installationConfig={installationConfig.data!}
            queryIsLoading={installationConfig.isFetching}
            hasNoStorage={hasNoStorage}
          />
        }
      />

      <Route
        path={SETTINGS_ROUTES.boiler}
        element={<BoilerPageWrapper installationId={installationIdNumber} myPv={parsedMyPv} queryIsLoading={installationConfig.isFetching} />}
      />
    </Routes>
  )
}

export default EndUserSettingsRoutes

interface InstallationQueryReturnType {
  installation: UseQueryResult<Nullable<InstallationDto>, Error>
  installationConfig: UseQueryResult<Nullable<InstallationConfigDto>, Error>
  badges: UseQueryResult<Nullable<BadgeDto[]>, Error>
  devices: UseQueryResult<Nullable<InstallationDevices>, Error>
  subDevices: UseQueryResult<Nullable<SubDevicesDto>, Error>
  isLoading: boolean
}

/**
 * Fetch all installation data needed for simple settings.
 * Done in multiple queries so they can be invalidated separately.
 */
const useInstallationQuery = (installationId: number): InstallationQueryReturnType => {
  const installation = useInstallation(installationId)
  const installationConfig = useInstallationConfig(installationId)
  const badges = useGetBadges(installationId)
  const devices = useInstallationDevices(installationId)
  const emcId: string | null = devices.data?.emc?.id?.toString() ?? null
  const subDevices = useInstallationSubDevices(installationId, emcId)

  const isLoading = installation.isLoading || installationConfig.isLoading || badges.isLoading || devices.isLoading || subDevices.isLoading

  return {
    installation,
    installationConfig,
    badges,
    devices,
    subDevices,
    isLoading,
  }
}
